import { ProjectTranslator } from 'src/translators'
import {
  AssignedProjectEntity,
  AssignedProjectSearchContentEntity,
  ProjectDetailEntity,
  UpsertProjectFormEntity,
} from 'src/types'
import {
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectShowCommonPageMutation,
} from 'src/types/generated'

type CreateProjectInput = {
  formEntity: UpsertProjectFormEntity
}

type UpdateProjectInput = {
  projectId: string
  formEntity: UpsertProjectFormEntity
}

type ToUpsertFormEntity = {
  entity: ProjectDetailEntity
}

export const useProject = () => {
  const [createProjectMutation] = useCreateProjectMutation()
  const [updateProjectMutation] = useUpdateProjectMutation()
  const [deleteProjectMutation] = useDeleteProjectMutation()
  const [updateProjectShowCommonPageMutation] = useUpdateProjectShowCommonPageMutation()

  const createProject = async ({ formEntity }: CreateProjectInput): Promise<void> => {
    const input = ProjectTranslator.toUpsertRequest(formEntity)
    await createProjectMutation({ variables: { input } })
  }

  const updateProject = async ({ projectId, formEntity }: UpdateProjectInput): Promise<void> => {
    const input = ProjectTranslator.toUpsertRequest(formEntity)
    await updateProjectMutation({ variables: { id: projectId, input } })
  }

  const deleteProject = async ({ byId }: { byId: string }): Promise<void> => {
    await deleteProjectMutation({ variables: { projectId: byId } })
  }

  const updateProjectShowCommonPage = async ({
    projectId,
    showCommonPage,
  }: {
    projectId: string
    showCommonPage: boolean
  }): Promise<void> => {
    await updateProjectShowCommonPageMutation({
      variables: { projectId, params: { showCommonPage } },
    })
  }

  const toUpsertFormEntity = ({ entity }: ToUpsertFormEntity): UpsertProjectFormEntity => {
    return ProjectTranslator.toFormEntity(entity)
  }

  // projectがsearchContentにマッチしている場合trueを返す
  const getIsProjectMatch = (
    project: AssignedProjectEntity,
    searchContent: AssignedProjectSearchContentEntity
  ): boolean => {
    if (searchContent.word) {
      const searchWords: string[] = searchContent.word.split(/[\s,]/).filter(Boolean)
      return searchWords.some(word => project.name.toLowerCase().includes(word.toLowerCase()))
    }

    if (searchContent.selectedStatuses && searchContent.selectedStatuses.length > 0) {
      return searchContent.selectedStatuses?.includes(project.status.name)
    }

    if (searchContent.selectedCompassRoles && searchContent.selectedCompassRoles.length > 0) {
      return searchContent.selectedCompassRoles?.includes(project.compassRole.name)
    }

    return true
  }

  return {
    createProject,
    updateProject,
    deleteProject,
    updateProjectShowCommonPage,
    toUpsertFormEntity,
    getIsProjectMatch,
  }
}
